import React, { useState } from "react";

interface ImageObject {
  originalImage?: string;
  image?: string;
  thumbnailImage?: string;
  variants?: {
    webp?: { [key: string]: string };
    jpeg?: { [key: string]: string };
  };
}

interface ResponsiveImageProps {
  imageObject?: ImageObject;
  alt: string;
  sizes: string;
  className?: string;
  style?: React.CSSProperties;
  loading?: "lazy" | "eager";
  priority?: boolean;
  aspectRatio?: string;
  objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down";
  onClick?: () => void;
  fallbackSrc?: string;
}

const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  imageObject,
  alt,
  sizes,
  className = "",
  style = {},
  loading = "lazy",
  priority = false,
  aspectRatio,
  objectFit = "cover",
  onClick,
  fallbackSrc = "/images/img-placeholder.svg",
}) => {
  const [useFallback, setUseFallback] = useState(false);

  const generateSrcSet = (variants: { [key: string]: string }): string => {
    return Object.entries(variants)
      .filter(([, url]) => url && url.trim() && url.startsWith("http")) // Filter out empty and invalid URLs
      .map(([size, url]) => {
        const width = size.replace("w", "");
        return `${url} ${width}w`;
      })
      .join(", ");
  };

  const imageStyle: React.CSSProperties = {
    width: "100%",
    height: "auto",
    objectFit,
    ...style,
    ...(aspectRatio && { aspectRatio }),
  };

  // Generate srcsets immediately without async validation
  const webpVariants = imageObject?.variants?.webp || {};
  const jpegVariants = imageObject?.variants?.jpeg || {};
  const webpSrcSet = generateSrcSet(webpVariants);
  const jpegSrcSet = generateSrcSet(jpegVariants);

  // Determine the best fallback image
  const getFallbackSrc = () => {
    if (useFallback) return fallbackSrc;

    // Prioritize originalImage first as it's most reliable
    if (imageObject?.originalImage && imageObject.originalImage.trim()) {
      return imageObject.originalImage;
    }

    // If no originalImage, try the image field
    if (imageObject?.image && imageObject.image.trim()) {
      return imageObject.image;
    }

    // Try to use the largest available valid variant as fallback
    const allVariants = { ...jpegVariants, ...webpVariants };
    const validVariants = Object.entries(allVariants).filter(
      ([, url]) => url && url.trim() && url.startsWith("http")
    );

    if (validVariants.length > 0) {
      const sortedSizes = validVariants
        .map(([size]) => parseInt(size.replace("w", "")))
        .sort((a, b) => b - a); // Sort descending

      const largestSize = `${sortedSizes[0]}w`;
      const largestVariant = allVariants[largestSize];
      if (largestVariant) {
        return largestVariant;
      }
    }

    return fallbackSrc;
  };

  return (
    <picture className={className} onClick={onClick}>
      {webpSrcSet && webpSrcSet.length > 0 && (
        <source srcSet={webpSrcSet} sizes={sizes} type="image/webp" />
      )}
      {jpegSrcSet && jpegSrcSet.length > 0 && (
        <source srcSet={jpegSrcSet} sizes={sizes} type="image/jpeg" />
      )}
      <img
        src={getFallbackSrc()}
        alt={alt}
        loading={priority ? "eager" : loading}
        style={imageStyle}
        onError={(e) => {
          console.warn(
            `Image failed to load: ${(e.target as HTMLImageElement).src}`
          );
          setUseFallback(true);
        }}
      />
    </picture>
  );
};

export default ResponsiveImage;
